import React, { useState, useEffect, useRef } from 'react'
import { CloudUpload, X } from 'lucide-react'

import { cn } from '../../../global/utils/cn.js'
import fileUtils from '../../../global/utils/fileUtils.js'
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../../global/components/ui/alert.jsx'
import { Badge } from '../../../global/components/ui/badge.jsx'
import { Button } from '../../../global/components/ui/button.jsx'
import { Card } from '../../../global/components/ui/card.jsx'
import { Spinner } from '../../../global/components/ui/spinner.jsx'
import {
  SUPPORTED_FORMATS_TEXT,
  MAX_FILE_SIZE_TEXT,
} from '../constants/fileConstants.js'

const MATCHING_STEPS = [50, 60, 70, 80, 85, 90, 93, 96, 98]

const FileUploadArea = ({
  files,
  dragOver,
  setDragOver,
  onFileUpload,
  onDrop,
  onRemoveFile,
  submitting,
  uploadItems,
  onSubmitFiles,
  hasDocuments,
  isMatchingFiles,
}) => {
  const validFiles = files.filter(f => f.isValidFile)
  const [matchingProgressMap, setMatchingProgressMap] = useState({})
  const [fileProgressOffsets, setFileProgressOffsets] = useState({})
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (isMatchingFiles && uploadItems) {
      const offsets = {}
      Object.values(uploadItems).forEach(item => {
        if (!fileProgressOffsets[item.name]) {
          offsets[item.name] = Math.floor(Math.random() * 3)
        }
      })
      if (Object.keys(offsets).length > 0) {
        setFileProgressOffsets(prev => ({ ...prev, ...offsets }))
      }
    }
  }, [isMatchingFiles, uploadItems])

  useEffect(() => {
    if (!isMatchingFiles || !uploadItems) {
      return
    }

    const interval = setInterval(() => {
      setMatchingProgressMap(prev => {
        const updated = { ...prev }

        Object.values(uploadItems).forEach(item => {
          if (item.percent === 100) {
            const current = prev[item.name] ?? 50
            const offset = fileProgressOffsets[item.name] ?? 0

            let targetStep = MATCHING_STEPS.find(step => step > current)

            if (targetStep) {
              const stepIndex = MATCHING_STEPS.indexOf(targetStep)
              const adjustedIndex = Math.min(
                MATCHING_STEPS.length - 1,
                stepIndex + offset,
              )
              targetStep = MATCHING_STEPS[adjustedIndex]
            }

            if (targetStep && targetStep > current) {
              updated[item.name] = targetStep
            }
          }
        })

        return updated
      })
    }, 600)

    return () => clearInterval(interval)
  }, [isMatchingFiles, uploadItems, fileProgressOffsets])

  const getUploadInfo = fileName => {
    if (!uploadItems) {
      return null
    }
    return Object.values(uploadItems).find(item => item.name === fileName)
  }

  const isBusy = submitting || isMatchingFiles

  return (
    <>
      <Card
        onDragOver={e => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={cn(
          'tw-mt-6 tw-flex tw-flex-col tw-items-center tw-justify-center tw-border-dashed tw-p-8 tw-text-center tw-shadow-none tw-transition-colors',
          dragOver && 'tw-bg-accent',
        )}
      >
        <CloudUpload
          aria-hidden="true"
          className="u-muted tw-size-9"
        />
        <p className="tw-mb-0 tw-mt-4">Drop Files Here</p>
        {hasDocuments && (
          <p className="u-muted tw-mb-0 tw-text-sm">
            Don&apos;t worry, we&apos;ll automatically match them to request
            list
          </p>
        )}
        <Button
          variant="outline"
          className="tw-mt-4"
          onClick={() => fileInputRef.current?.click()}
        >
          Select file
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="tw-sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={onFileUpload}
        />
      </Card>

      {files.length > 0 && (
        <div className="tw-mt-6">
          <h5 className="tw-mb-2">
            Selected Files ({validFiles.length} valid)
          </h5>
          {files.map((file, index) => {
            const uploadInfo = getUploadInfo(file.name)

            let displayPercent = 0
            let statusText = ''

            if (uploadInfo) {
              if (uploadInfo.failedMessages?.length) {
                displayPercent = uploadInfo.percent * 0.5
                statusText = uploadInfo.failedMessages[0]
              } else if (uploadInfo.percent < 100) {
                displayPercent = uploadInfo.percent * 0.5
                statusText = `Uploading ${uploadInfo.percent}%`
              } else if (isMatchingFiles) {
                const matchPercent = matchingProgressMap[file.name] ?? 50
                displayPercent = matchPercent
                statusText = `Matching ${matchPercent}%`
              } else {
                displayPercent = 50
                statusText = 'Upload complete'
              }
            }

            return (
              <div
                key={file._id}
                className="tw-m-1 tw-inline-block tw-align-top"
              >
                <div className="tw-flex tw-items-center tw-gap-1">
                  <Badge variant={file.isValidFile ? 'outline' : 'destructive'}>
                    {file.name} ({fileUtils.formatFileSize(file.size)})
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="tw-h-6 tw-w-6"
                    aria-label={`Remove ${file.name}`}
                    disabled={isBusy}
                    onClick={() => onRemoveFile(index)}
                  >
                    <X aria-hidden="true" />
                  </Button>
                </div>
                {uploadInfo && (
                  <>
                    <div className="tw-mt-1 tw-h-[3px] tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-muted">
                      <div
                        className={cn(
                          'tw-h-full tw-transition-[width] tw-duration-300',
                          uploadInfo.failedMessages
                            ? 'tw-bg-destructive'
                            : 'tw-bg-primary',
                        )}
                        style={{ width: `${displayPercent}%` }}
                      />
                    </div>
                    <small className="tw-mt-0.5 tw-block tw-text-[0.65rem]">
                      {statusText}
                    </small>
                  </>
                )}
              </div>
            )
          })}
          {isBusy && (
            <Alert
              variant="info"
              className="tw-my-4 tw-flex tw-items-center tw-gap-3"
            >
              <Spinner
                size="sm"
                label="Processing files"
              />
              <div>
                <AlertTitle className="tw-mb-0.5">Processing Files</AlertTitle>
                <AlertDescription>
                  {submitting
                    ? 'Your files are being uploaded...'
                    : isMatchingFiles
                      ? 'Your files are being matched to documents...'
                      : ''}
                </AlertDescription>
              </div>
            </Alert>
          )}
        </div>
      )}

      <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-gap-4">
        <small className="u-muted">
          Supported format: {SUPPORTED_FORMATS_TEXT}
        </small>
        <small className="u-muted">
          Maximum file size: {MAX_FILE_SIZE_TEXT}
        </small>
      </div>

      <div className="tw-mt-6 tw-text-right">
        <Button
          disabled={submitting || validFiles.length === 0 || isMatchingFiles}
          onClick={onSubmitFiles}
        >
          {submitting ? 'Uploading...' : 'Submit Files'}
        </Button>
      </div>
    </>
  )
}

export default FileUploadArea
